import axios from 'axios';
import { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { FormValidationContainer } from '../../components/Form/FormValidatorContainer';
import { Loader } from '../../components/Loader/Loader';
import { useConfig } from '../../config';
import { useAnalytics } from '../../hooks/useAnalytics';
import { useCriteo } from '../../hooks/useCriteo';
import { useTranslation } from '../../i18n/i18n';
import { useApi, useCacheInvalidator } from '../../state/api';
import { Res } from '../../state/api-resources';
import { useCart } from '../../state/cart';
import { CheckoutContainer } from '../../state/checkout';
import { useSession } from '../../state/session';
import { UiContainer } from '../../state/ui';
import { ApiActionResponse, ApiCheckoutState } from '../../types';
import { ContentCard } from '../ContentCard/ContentCard';
import { CustomerTypeInput, CustomerTypeModel } from '../Form/CustomerTypeInput';
import { Main } from '../Main/Main';
import { Routing } from '../Routes';
import { CheckoutCompany } from '../checkout/CheckoutCompany';
import { CheckoutPrivate } from '../checkout/CheckoutPrivate';
import { buildCheckoutState } from '../checkout/utils';
import './CheckoutPage.css';

export const CheckoutPage = (): JSX.Element => {
	const t = useTranslation().checkout;
	const { user } = useSession();
	const { data } = useApi(Res.checkoutState, {});
	const checkoutState = data ? buildCheckoutState(data) : undefined;
	const { sendEventOnce } = useAnalytics();
	const { sendCriteoEventOnce, criteoId } = useCriteo();
	const { api, store, currencyCode } = useConfig();
	const { cart } = useCart();
	const ui = UiContainer.useContainer();
	const [homeDeliveryEnabled, setHomeDeliveryEnabled] = useState(false);
	const mutate = useCacheInvalidator();
	const history = useHistory();
	const r = useTranslation().routes;

	async function postCheckoutState(payload: ApiCheckoutState): Promise<void> {
		try {
			await axios.post<ApiCheckoutState>(api + Res.checkoutState.uri({}), payload, { withCredentials: true });
		} catch (e) {
			// all four callers fire this and walk away, so the rejection has nowhere else to go
			return;
		}

		mutate(Res.checkoutState, {});
	}

	useEffect(() => {
		if ((store === 'kgse' || store === 'kgdk') && cart.items.length > 0) {
			const cartContainsNonDeliverableItems = !!cart.items.some(
				(i) =>
					(i.product.vendorId !== 25 && store === 'kgse') || (i.product.vendorId !== 22 && store === 'kgdk')
			);
			if (homeDeliveryEnabled !== !cartContainsNonDeliverableItems)
				setHomeDeliveryEnabled(!cartContainsNonDeliverableItems);
		}
	}, [cart.items, store]);

	useEffect(() => {
		if (!cart) return;
		sendEventOnce({
			event: 'begin_checkout',
			ecommerce: {
				currency: currencyCode,
				value: cart.totals?.total || 0,
				items: cart.items.map((item) => ({
					item_id: String(item.product.id),
					item_name: item.product.name,
					price: item.price.currentPrice,
				})),
			},
		});
		if (!!criteoId) {
			sendCriteoEventOnce({
				event: 'viewBasket',
				item: cart.items.map((item) => {
					return {
						id: item.product.id + '',
						name: item.product.name,
						quantity: item.quantity,
						price: item.price.currentPrice + '',
					};
				}),
			});
		}
	}, [!!cart]);

	useEffect(() => {
		if (!!user?.punchout) history.push(r.punchoutCart);
	}, [user]);

	function handleSetCustomerType(model: CustomerTypeModel) {
		if (!checkoutState) return;
		const { customerType } = model;
		ui.setPriceWithVat(customerType === 'private');
		ui.setCustomerType(customerType);

		axios
			.post<ApiActionResponse>(`${api}customer-type.json`, customerType, {
				withCredentials: true,
			})
			// a background sync of state the ui has already applied; the rejection has to be taken
			.catch(() => undefined);

		if (customerType === 'private') {
			const deliveryType = checkoutState.delivery.type === 'home' ? 'home' : 'servicepoint';
			postCheckoutState({
				...checkoutState,
				customerType,
				delivery: { ...checkoutState.delivery, type: deliveryType },
			});
		} else {
			postCheckoutState({ ...checkoutState, customerType });
		}
	}

	if (!checkoutState) return <Loader />;

	const isPrivateView = checkoutState.customerType === 'private';

	return (
		<Main>
			<div id="checkout-page">
				<h1 className="private-page-heading">{t.checkout}</h1>
				<div>
					<ContentCard title={t.customerType}>
						<CustomerTypeInput model={checkoutState} onChange={handleSetCustomerType} />
					</ContentCard>

					<FormValidationContainer.Provider>
						<div>
							{isPrivateView ? (
								<CheckoutPrivate
									state={checkoutState}
									onChange={(model) => postCheckoutState(model)}
									homeDeliveryEnabled={homeDeliveryEnabled}
								/>
							) : (
								<CheckoutCompany
									state={checkoutState}
									totals={cart.totals}
									onChange={(model) => postCheckoutState(model)}
									homeDeliveryEnabled={homeDeliveryEnabled}
								/>
							)}
						</div>
					</FormValidationContainer.Provider>
				</div>
			</div>
		</Main>
	);
};

function PageWithCheckoutContainer() {
	return (
		<CheckoutContainer.Provider>
			<CheckoutPage />
		</CheckoutContainer.Provider>
	);
}

export const checkoutRouting: Routing = {
	path(urls) {
		return urls.checkout;
	},
	component: PageWithCheckoutContainer,
	requiresAuth: false,
};
